import { useState, useEffect, useCallback } from 'react';

export interface UseMenuPullCacheOptions {
  restaurantId: string | null;
}

export interface UseMenuPullCacheReturn {
  hasMenuBeenPulled: boolean;
  markMenuAsPulled: () => void;
  isValidCache: boolean;
}

// localStorage key - stores only the restaurant ID
const MENU_PULLED_RESTAURANT_ID_KEY = 'menu_pulled_restaurant_id';

/**
 * Custom hook to manage menu pull cache with restaurant ID validation
 * 
 * Stores only the restaurant ID. If stored ID matches current ID, cache is valid.
 * 
 * @param options - Configuration options
 * @returns Cache status and management functions
 * 
 * @example
 * const { isValidCache, markMenuAsPulled } = useMenuPullCache({
 *   restaurantId: '12345'
 * });
 * 
 * // Check if menu was pulled for THIS restaurant
 * if (isValidCache) {
 *   // Skip loader - same restaurant
 * } else {
 *   // Show loader - different restaurant or first time
 * }
 * 
 * // Mark as pulled after successful pull
 * markMenuAsPulled();
 */
export const useMenuPullCache = (
  options: UseMenuPullCacheOptions
): UseMenuPullCacheReturn => {
  const { restaurantId } = options;

  // Initialize to false to prevent race conditions
  const [isValidCache, setIsValidCache] = useState(false);

  // Validate cache when restaurantId changes
  useEffect(() => {
    if (!restaurantId) {
      setIsValidCache(false);
      return;
    }
    
    // Get stored restaurant ID
    const storedRestaurantId = localStorage.getItem(MENU_PULLED_RESTAURANT_ID_KEY);
    
    // Validate: stored ID must match current ID
    const isValid = storedRestaurantId === restaurantId;
    
    setIsValidCache(isValid);
  }, [restaurantId]);

  // Mark menu as pulled - store only restaurant ID
  const markMenuAsPulled = useCallback(() => {
    if (restaurantId) {
      localStorage.setItem(MENU_PULLED_RESTAURANT_ID_KEY, restaurantId);
      setIsValidCache(true);
    }
  }, [restaurantId]);

  return {
    hasMenuBeenPulled: isValidCache, // For backward compatibility
    markMenuAsPulled,
    isValidCache,
  };
};
